import { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Image from '../../../components/AppImage';
import Button from '../../../components/ui/Button';

const DocumentPreview = ({ document, onClose }) => {
  const [activeTab, setActiveTab] = useState('preview');

  if (!document) {
    return (
      <div className="h-full flex items-center justify-center bg-card border-l border-border">
        <div className="text-center">
          <Icon name="FileText" size={64} color="var(--color-muted-foreground)" />
          <p className="text-sm text-muted-foreground mt-4">
            Select a document to preview
          </p>
        </div>
      </div>
    );
  }

  const formatDate = (date) => {
    return new Date(date)?.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatFileSize = (bytes) => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024)?.toFixed(1) + ' KB';
    return (bytes / (1024 * 1024))?.toFixed(1) + ' MB';
  };

  return (
    <div className="h-full flex flex-col bg-card border-l border-border">
      <div className="flex items-center justify-between p-4 border-b border-border">
        <h3 className="text-sm font-semibold text-foreground truncate">
          {document?.name}
        </h3>
        <button
          onClick={onClose}
          className="p-1.5 rounded-md hover:bg-accent/10 transition-colors"
          aria-label="Close preview"
        >
          <Icon name="X" size={18} color="var(--color-muted-foreground)" />
        </button>
      </div>
      <div className="flex border-b border-border">
        <button
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'preview' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('preview')}
        >
          Preview
        </button>
        <button
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'details' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('details')}
        >
          Details
        </button>
        <button
          className={`flex-1 px-4 py-2 text-sm font-medium transition-colors ${
            activeTab === 'activity' ?'text-primary border-b-2 border-primary' :'text-muted-foreground hover:text-foreground'
          }`}
          onClick={() => setActiveTab('activity')}
        >
          Activity
        </button>
      </div>
      <div className="flex-1 overflow-y-auto custom-scrollbar">
        {activeTab === 'preview' && (
          <div className="p-4">
            {document?.thumbnail ? (
              <div className="w-full rounded-lg overflow-hidden bg-muted">
                <Image
                  src={document?.thumbnail}
                  alt={document?.thumbnailAlt}
                  className="w-full h-auto"
                />
              </div>
            ) : (
              <div className="w-full h-96 rounded-lg bg-muted flex items-center justify-center">
                <div className="text-center">
                  <Icon name="FileText" size={64} color="var(--color-muted-foreground)" />
                  <p className="text-sm text-muted-foreground mt-4">
                    Preview not available
                  </p>
                </div>
              </div>
            )}
            <div className="mt-4 flex gap-2">
              <Button variant="outline" size="sm" iconName="Download" fullWidth>
                Download
              </Button>
              <Button variant="outline" size="sm" iconName="Share2" fullWidth>
                Share
              </Button>
            </div>
          </div>
        )}

        {activeTab === 'details' && (
          <div className="p-4 space-y-4">
            <div>
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                File Information
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Type</span>
                  <span className="text-sm font-medium text-foreground">
                    {document?.type?.toUpperCase()}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Size</span>
                  <span className="text-sm font-medium text-foreground">
                    {formatFileSize(document?.size)}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Version</span>
                  <span className="text-sm font-medium text-foreground">
                    v{document?.version}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Category</span>
                  <span className="text-sm font-medium text-foreground">
                    {document?.category}
                  </span>
                </div>
              </div>
            </div>

            <div className="border-t border-border pt-4">
              <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                Upload Details
              </h4>
              <div className="space-y-3">
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Uploaded By</span>
                  <span className="text-sm font-medium text-foreground">
                    {document?.uploadedBy}
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-sm text-muted-foreground">Upload Date</span>
                  <span className="text-sm font-medium text-foreground">
                    {formatDate(document?.uploadDate)}
                  </span>
                </div>
              </div>
            </div>

            {document?.linkedShipment && (
              <div className="border-t border-border pt-4">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                  Linked Shipment
                </h4>
                <div className="p-3 bg-muted rounded-lg">
                  <p className="text-sm font-medium text-foreground">
                    {document?.linkedShipment}
                  </p>
                  <p className="text-xs text-muted-foreground mt-1">
                    Vessel: {document?.vessel}
                  </p>
                </div>
              </div>
            )}

            {document?.tags && document?.tags?.length > 0 && (
              <div className="border-t border-border pt-4">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                  Tags
                </h4>
                <div className="flex flex-wrap gap-2">
                  {document?.tags?.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-primary/10 text-primary text-xs rounded-md"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}

            {document?.description && (
              <div className="border-t border-border pt-4">
                <h4 className="text-xs font-semibold text-muted-foreground uppercase mb-2">
                  Description
                </h4>
                <p className="text-sm text-foreground">{document?.description}</p>
              </div>
            )}
          </div>
        )}

        {activeTab === 'activity' && (
          <div className="p-4">
            <div className="space-y-4">
              {document?.activity?.map((item, index) => (
                <div key={index} className="flex gap-3">
                  <div className="flex-shrink-0 w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center">
                    <Icon name={item?.icon} size={16} color="var(--color-primary)" />
                  </div>
                  <div className="flex-1">
                    <p className="text-sm text-foreground">{item?.action}</p>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-xs text-muted-foreground">
                        {item?.user}
                      </span>
                      <span className="text-xs text-muted-foreground">•</span>
                      <span className="text-xs text-muted-foreground">
                        {formatDate(item?.timestamp)}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
      <div className="p-4 border-t border-border">
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" iconName="Trash2" className="flex-1">
            Delete
          </Button>
          <Button variant="default" size="sm" iconName="Edit" className="flex-1">
            Edit
          </Button>
        </div>
      </div>
    </div>
  );
};

export default DocumentPreview;